/**
 * advanceDeepLink.ts — where a merge-field pre-flight on the advance dossier sends the admin.
 *
 * TOTAL OVER THE VOCABULARY, WHICH IS THE WHOLE REASON IT IS A MODULE AND NOT A CLOSURE. The
 * Schedule's pre-flight draws one "Resolve in …" control per unresolved field and every one of them
 * calls this; handed a key it has no answer for, an inline handler would paint an enabled button
 * that does nothing. Loan v7 quotes eighteen merge keys drawn from five namespaces — `loan.`,
 * `borrower.`, `vehicle.`, `contract.` and `company.` — and only the first of those is edited on
 * this flow at all. So the answer is one of exactly two shapes and never `undefined`: an area of
 * this dossier, resolved in place, or a SETUP path, which is a real route even when the key names
 * nothing (`areaPath(id, null)` is the bare setup route, and the landing rule takes it from there).
 *
 * THE TWO VOCABULARIES ARE CONSULTED IN ORDER, NEVER MERGED. `resolveAdvanceArea` answers first
 * because `MERGE_FIELD_STEPS` routes the `loan.` namespace to this dossier's own keys; everything
 * else falls through to `resolveArea`, which is `SetupArea`'s closed union of six. Neither function
 * learns about the other's keys — `labelForArea` is the same arrangement for the sentence.
 *
 * PURE, AND REACT-FREE: it composes a path rather than navigating to one, so a test can assert the
 * totality without a router.
 */

import { stepForMergeField } from '@/lib/agreements/constants';

import { areaPath, resolveArea } from '../steps';
import { resolveAdvanceArea, type AdvanceArea } from './advanceSteps';

export type AdvanceDeepLink =
    /** An area of this dossier: the canvas changes, the page does not. */
    | { kind: 'advance'; area: AdvanceArea }
    /** Anywhere else: the contract's own setup route, always a real path. */
    | { kind: 'setup'; path: string };

/**
 * The destination for an AREA KEY — what `AgreementSlotList` hands its `onDeepLink`.
 *
 * `blockedStep` and `stepForMergeField` both answer with a key rather than a route, and the two
 * vocabularies share that one type. Anything unrecognised is a setup link to the bare route rather
 * than a refusal: an admin sent to the top of the contract's own dossier can still find the field,
 * and an admin sent nowhere cannot.
 */
export function resolveAdvanceDeepLink(
    contractId: number,
    value: string | null | undefined,
): AdvanceDeepLink {
    const advance = resolveAdvanceArea(value);
    if (advance) return { kind: 'advance', area: advance };
    return { kind: 'setup', path: areaPath(contractId, resolveArea(value)) };
}

/**
 * The destination for a MERGE FIELD — the same answer, one lookup earlier.
 *
 * It exists so nothing has to spell `resolveAdvanceDeepLink(id, stepForMergeField(field))` twice
 * and get the composition backwards in one of them.
 */
export function advanceDeepLinkForMergeField(contractId: number, field: string): AdvanceDeepLink {
    return resolveAdvanceDeepLink(contractId, stepForMergeField(field));
}
